/**
 * Feedback Triage — the open reports from #people_spaces_feedback, with an owner on each one.
 *
 * The feedback button has been posting into that channel since 14 Aug 2026 and nothing has ever
 * read the pile back. This page does: it parses the bot's own posts, drops everything already
 * ticked off, and adds the three things a Slack thread cannot hold — who owns it, where it
 * stands, and what we decided.
 *
 * ── One rule runs the whole page ────────────────────────────────────────────────────────────
 * A report carrying :white_check_mark: in Slack is closed and never arrives here. So there is no
 * "closed" tab and no archive: this list is work, and it gets shorter. Setting a report to Fixed
 * puts the checkmark ON the Slack post, which is why the row then leaves — the page and the
 * channel are saying the same thing rather than keeping two ledgers. Triaged and In progress put
 * the team's own :onit1: on the post for the same reason.
 *
 * ── Why the owners are hardcoded ────────────────────────────────────────────────────────────
 * Four named people take these tickets. A directory picker would let anyone be assigned work they
 * have never agreed to; the closed list lives in the proxy and is enforced there, and this view
 * only renders what it is given (`owners` off the API, never a local copy).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { useAuth } from '../../../contexts/AuthContext';
import { CockpitTabs } from './taCockpit/cockpitTabs';
import { BLUE_BUTTON, BUTTON_CONTENT } from './orgPlanningChrome';
import {
  fetchReports,
  patchReport,
  postThreadReply,
  type FeedbackReport,
  type OwnerId,
  type ReportsResponse,
  type TriagePatch,
  type TriageStatus,
} from '../services/feedbackTriageApi';
import { ReportRow } from './feedbackTriage/ReportRow';
import { ReportDetail } from './feedbackTriage/ReportDetail';
import { ResolveDialog } from './feedbackTriage/ResolveDialog';
import { OwnerCards } from './feedbackTriage/OwnerCards';
import { PiaLessons } from './feedbackTriage/PiaLessons';
import { ACTIVE_PIA_PHASES, CLOSING_STATUSES, RADIUS, STATUS_LABELS } from './feedbackTriage/triageTokens';

const QUERY_KEY = ['feedback-triage'];

type Tab = 'all' | 'pia' | TriageStatus;

const TABS: { value: Tab; label: string }[] = [
  { value: 'all', label: 'All open' },
  { value: 'pia', label: 'With Pia' },
  { value: 'new', label: 'New' },
  { value: 'triaged', label: 'Triaged' },
  { value: 'progress', label: 'In progress' },
  { value: 'waiting', label: 'Needs decision' },
];

const TYPE_FILTERS: FeedbackReport['type'][] = ['bug', 'improvement', 'feature', 'other'];

const SELECT =
  `${RADIUS} border border-zinc-200 bg-white px-2 py-1.5 text-[12.5px] text-zinc-900 ` +
  'focus:outline-none focus:ring-2 focus:ring-blue-500';

function matchesTab(report: FeedbackReport, tab: Tab): boolean {
  if (tab === 'all') return true;
  if (tab === 'pia') return !!report.pia && ACTIVE_PIA_PHASES.includes(report.pia.phase);
  return report.status === tab;
}

export default function FeedbackTriageView() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('all');
  const [types, setTypes] = useState<FeedbackReport['type'][]>([]);
  const [ownerFilter, setOwnerFilter] = useState<'all' | OwnerId>('all');
  const [surface, setSurface] = useState('all');
  const [pending, setPending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [openTs, setOpenTs] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // The report just marked Fixed, held here because closing drops it from the list — the dialog
  // still needs its decision, its fix link and its thread.
  const [resolved, setResolved] = useState<FeedbackReport | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);

  const { user } = useAuth();
  const viewerEmail = (user?.email || '').toLowerCase();

  const { data, isPending, error, isFetching } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => fetchReports(),
    // The proxy caches the Slack read for 45s of its own; this keeps two people on the board from
    // re-fetching each other's writes on every focus.
    staleTime: 30 * 1000,
  });

  /**
   * Refresh re-reads Slack rather than re-reading the proxy's 45-second cache. That cache is there
   * to stop a room full of people burning the rate limit, but Refresh is pressed exactly when
   * somebody has just ticked a report off in the channel and wants to see it go.
   */
  async function refresh() {
    setRefreshing(true);
    try {
      queryClient.setQueryData<ReportsResponse>(QUERY_KEY, await fetchReports(true));
      setActionError(null);
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  // Above the mutations because `save` reads it: defined after, the compiler cannot keep the memo.
  const reports = useMemo(() => data?.reports ?? [], [data]);
  const owners = data?.owners ?? [];

  const save = useMutation({
    mutationFn: ({ ts, patch }: { ts: string; patch: TriagePatch }) => patchReport(ts, patch),
    onMutate: ({ ts }) => {
      setActionError(null);
      setPending(ts);
    },
    onSuccess: (result, { ts, patch }) => {
      // Slack accepted the status but not the reaction: the board is right and the channel is
      // not, which is precisely the mismatch a silent success would hide.
      if (result.warnings?.length) setActionError(result.warnings.join(' '));

      if (patch.status && CLOSING_STATUSES.includes(patch.status)) {
        // Closing writes the checkmark, so the report is gone from the next read. Dropping the row
        // now rather than waiting for the refetch is what makes the list feel like a queue.
        queryClient.setQueryData<ReportsResponse>(QUERY_KEY, (old) =>
          old ? { ...old, reports: old.reports.filter((r) => r.ts !== ts) } : old
        );
        // The report it was showing no longer exists, so the drawer has nothing left to show.
        setOpenTs((open) => (open === ts ? null : open));
        if (patch.status === 'done') {
          // The report it belongs to has just left the list, so the dialog carries its own copy.
          const report = reports.find((r) => r.ts === ts);
          setReplyError(null);
          setResolved(report ? { ...report, status: 'done' } : null);
        }
        showToast({
          status: 'success',
          description:
            patch.status === 'done'
              ? 'Ticked off in Slack — the report is closed for everyone.'
              : 'Marked as not a report and ticked off in Slack.',
        });
        return;
      }

      // The proxy re-derives the whole row (owner and its source, effective priority/type, Pia),
      // so it replaces ours wholesale; only the permalink is not on it. Without a row the report
      // is no longer open on the proxy's side, and a re-read is the honest answer.
      if (!result.report) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        return;
      }
      const row = result.report;
      queryClient.setQueryData<ReportsResponse>(QUERY_KEY, (old) =>
        old
          ? {
              ...old,
              reports: old.reports.map((r) => (r.ts === ts ? { ...row, permalink: r.permalink } : r)),
            }
          : old
      );
    },
    onError: (err: Error) => setActionError(err.message),
    onSettled: () => setPending(null),
  });

  /**
   * The closing note. Deliberately its own mutation rather than part of the save: the status is
   * already committed and the checkmark already on the post, so a Slack refusal here must read as
   * "the note did not go out", never as "the report is not closed".
   */
  const reply = useMutation({
    mutationFn: ({ ts, text }: { ts: string; text: string }) => postThreadReply(ts, text),
    onMutate: () => setReplyError(null),
    onSuccess: () => {
      setResolved(null);
      showToast({ status: 'success', description: 'Posted in the thread.' });
    },
    onError: (err: Error) => setReplyError(err.message),
  });


  const surfaces = useMemo(
    () => Array.from(new Set(reports.map((r) => r.area))).sort(),
    [reports]
  );

  const shown = useMemo(
    () =>
      reports
        .filter((r) => matchesTab(r, tab))
        .filter((r) => types.length === 0 || types.includes(r.type))
        .filter((r) => ownerFilter === 'all' || r.owner === ownerFilter)
        .filter((r) => surface === 'all' || r.area === surface)
        // Newest first (Theresa, Sep 2026): the list is worked through by hand, and the newest
        // report is the one whose context is still fresh. The oldest are not lost — Age is a column.
        .sort((a, b) => Number(b.ts) - Number(a.ts)),
    [reports, tab, types, ownerFilter, surface]
  );

  // Read out of the list rather than held in state, so an edit saved on the row is on the drawer.
  const opened = reports.find((r) => r.ts === openTs) ?? null;

  /** Clicking an owner card; clicking the active one again clears the filter. */
  function pickOwnerCard(id: OwnerId) {
    setOwnerFilter(ownerFilter === id ? 'all' : id);
  }

  if (isPending) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="p-6">
        <div className={`flex items-start gap-3 border border-red-200 bg-red-50 p-4 ${RADIUS}`}>
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-red-600" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-red-800">The feedback channel could not be read</p>
            <p className="mt-1 text-sm text-red-700">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Feedback Triage</h1>
          <p className="mt-1 max-w-3xl text-sm text-zinc-500">
            Every open report the feedback button sent to{' '}
            <span className="font-semibold text-zinc-700">#people_spaces_feedback</span>. Anything
            already ticked off with ✅ in Slack is closed and does not show up here. Every report has
            an owner — by page until somebody confirms — and shows where Pia is with it.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            {data?.channelUrl && (
              <a
                href={data.channelUrl}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center gap-1.5 ${RADIUS} border border-zinc-200 px-3 py-1.5 text-[12.5px] text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900`}
              >
                Open channel
                <ExternalLink className="size-3" aria-hidden />
              </a>
            )}
            <Button
              variant="outline"
              className={BLUE_BUTTON}
              onClick={() => refresh()}
              disabled={isFetching || refreshing}
            >
              <span className={`flex items-center ${BUTTON_CONTENT}`}>
                <RefreshCw
                  className={`size-4 ${isFetching || refreshing ? 'animate-spin' : ''}`}
                  aria-hidden
                />
                Refresh
              </span>
            </Button>
          </div>
          <p className="text-[12.5px] text-zinc-500">
            <span className="font-bold text-zinc-900">{reports.length} open</span>
            {data?.syncedAt && ` · synced ${new Date(data.syncedAt).toLocaleTimeString()}`}
          </p>
        </div>
      </header>

      {actionError && (
        <div className={`flex items-start gap-2 border border-amber-200 bg-amber-50 p-3 ${RADIUS}`}>
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
          <p className="text-[13px] text-amber-800">{actionError}</p>
        </div>
      )}

      {/* Who is carrying what. */}
      <OwnerCards
        owners={owners}
        reports={reports}
        ownerFilter={ownerFilter}
        viewerEmail={viewerEmail}
        onPickOwner={pickOwnerCard}
      />

      {/* What the team told Pia to do on every report; only shown when there is something. */}
      <PiaLessons />

      <section
        aria-label="Filters"
        className={`flex flex-wrap items-center gap-3 border border-zinc-200 bg-white p-2.5 ${RADIUS}`}
      >
        <CockpitTabs
          value={tab}
          tabs={TABS}
          onChange={(next) => setTab(next)}
          shape="soft"
          ariaLabel="Status"
        />
        <div className="flex gap-1.5" role="group" aria-label="Type">
          {TYPE_FILTERS.map((t) => {
            const on = types.includes(t);
            return (
              <button
                key={t}
                type="button"
                aria-pressed={on}
                onClick={() => setTypes(on ? types.filter((x) => x !== t) : [...types, t])}
                className={`rounded-full border px-3 py-1 text-[12.5px] capitalize ${
                  on
                    ? 'border-zinc-900 bg-zinc-900 text-white'
                    : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400'
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
        <div className="flex-1" />
        <label className="flex items-center gap-2 text-[12.5px] text-zinc-600">
          Owner
          <select
            className={SELECT}
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value as typeof ownerFilter)}
          >
            <option value="all">Everyone</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-[12.5px] text-zinc-600">
          Surface
          <select className={SELECT} value={surface} onChange={(e) => setSurface(e.target.value)}>
            <option value="all">All surfaces</option>
            {surfaces.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section
        aria-label="Reports"
        className={`flex min-h-0 flex-1 flex-col overflow-hidden border border-zinc-200 bg-white ${RADIUS}`}
      >
        <div
          className="grid gap-3 border-b border-zinc-200 bg-zinc-50 px-5 py-2.5 text-[10.5px] font-bold uppercase tracking-wider text-zinc-500"
          style={{ gridTemplateColumns: '54px minmax(0,1fr) 116px 70px 152px 150px 32px' }}
        >
          <div>Prio</div>
          <div>Report</div>
          <div>Type</div>
          <div>Age</div>
          <div>Owner</div>
          <div>Status</div>
          <div />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {shown.map((report) => (
            <ReportRow
              key={report.ts}
              report={report}
              owners={owners}
              busy={pending === report.ts}
              onPatch={(patch) => save.mutate({ ts: report.ts, patch })}
              onOpen={() => setOpenTs(report.ts)}
            />
          ))}
          {shown.length === 0 && (
            <p className="p-14 text-center text-sm text-zinc-500">
              {reports.length === 0
                ? 'Nothing open — every report in the channel is ticked off.'
                : 'Nothing matches these filters.'}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-zinc-200 bg-zinc-50 px-5 py-2.5">
          <p className="text-[12.5px] text-zinc-500">
            {shown.length} of {reports.length} open · {STATUS_LABELS.done} ticks the Slack post and
            closes the report
          </p>
        </div>
      </section>

      {resolved && (
        <ResolveDialog
          report={resolved}
          posting={reply.isPending}
          error={replyError}
          onPost={(text) => reply.mutate({ ts: resolved.ts, text })}
          onSkip={() => {
            setResolved(null);
            setReplyError(null);
          }}
        />
      )}

      {opened && (
        <ReportDetail
          // Keyed on the report, so opening a different one starts from ITS decision and fix link
          // rather than inheriting whatever was typed into the last one.
          key={opened.ts}
          report={opened}
          owners={owners}
          busy={pending === opened.ts}
          onClose={() => setOpenTs(null)}
          onSave={(patch) => save.mutate({ ts: opened.ts, patch })}
        />
      )}
    </div>
  );
}
